import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface Props {
  icon: LucideIcon;
  /** Small uppercase eyebrow, e.g. "Schedule Performance Domain". */
  label: string;
  /** Short editorial sentence. Avoid SaaS-cheery affirmations. */
  hint: string;
  /** Optional call-to-action button or other content rendered under the hint. */
  cta?: ReactNode;
  /** Tints the icon disc; defaults to a tri-color neon gradient. */
  tone?: "neon" | "muted" | "success";
  className?: string;
}

const TONE_DISC: Record<NonNullable<Props["tone"]>, string> = {
  neon:    "bg-gradient-to-br from-primary/15 via-accent/15 to-secondary/15 ring-primary/20",
  muted:   "bg-muted ring-border",
  success: "bg-success/10 ring-success/30",
};

/**
 * Editorial empty state with an icon disc, eyebrow label, hint, and optional
 * action slot. Used in tabs that currently have no rows, and as the fallback
 * when a list is empty.
 */
export function EmptyState({ icon: Icon, label, hint, cta, tone = "neon", className }: Props) {
  return (
    <Card className={className}>
      <CardContent className="p-10 text-center space-y-3">
        <div
          className={cn(
            "mx-auto size-16 rounded-full ring-2 grid place-items-center",
            TONE_DISC[tone],
          )}
        >
          <Icon size={28} strokeWidth={1.5} className="text-foreground/80" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground">
          {label}
        </div>
        <div className="text-sm text-muted-foreground max-w-xs mx-auto leading-relaxed">
          {hint}
        </div>
        {cta ? <div className="pt-2">{cta}</div> : null}
      </CardContent>
    </Card>
  );
}
